import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _PropTypes from 'prop-types';
import { detectOverflow } from '@popperjs/core';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import { useIsMounted } from '../../hooks/use_is_mounted.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { zIndex1050 } from '../../styles/zIndex.js';
import { noop } from '../../utils/util.js';
import { Resizer } from '../Resizer/Resizer.js';
import { Transition } from '../Transition/Transition.js';
import { ArrowPositioner } from './ArrowPositioner.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

var MAX_HEIGHT = 500;
var MIN_HEIGHT = 200;
var EXTRA_SPACE = 8;

var getMaxSize = function getMaxSize(_ref) {
  var state = _ref.state,
      name = _ref.name,
      options = _ref.options;
  var overflow = detectOverflow(state, options);
  var _state$modifiersData$ = state.modifiersData.preventOverflow,
      _state$modifiersData$2 = _state$modifiersData$.x,
      x = _state$modifiersData$2 === void 0 ? 0 : _state$modifiersData$2,
      _state$modifiersData$3 = _state$modifiersData$.y,
      y = _state$modifiersData$3 === void 0 ? 0 : _state$modifiersData$3;
  var _state$rects$popper = state.rects.popper,
      width = _state$rects$popper.width,
      height = _state$rects$popper.height;
  var basePlacement = state.placement.split("-")[0];
  var widthProp = basePlacement === "left" ? "left" : "right";
  var heightProp = basePlacement === "top" ? "top" : "bottom";
  state.modifiersData[name] = {
    width: width - overflow[widthProp] - x,
    height: height - overflow[heightProp] - y
  };
  return state;
};

var setMaxSize = function setMaxSize(_ref2) {
  var maxHeight = _ref2.maxHeight,
      maxHeightRef = _ref2.maxHeightRef,
      unrestrictedHeight = _ref2.unrestrictedHeight;
  return function (_ref3) {
    var state = _ref3.state;
    var _state$modifiersData$4 = state.modifiersData.getMaxSize,
        height = _state$modifiersData$4.height,
        width = _state$modifiersData$4.width;
    var max = unrestrictedHeight ? height : Math.min(height, maxHeight);
    var min = Math.max(height, MIN_HEIGHT);
    var nextHeight = "".concat(Math.min(min, Math.max(0, max)) - EXTRA_SPACE, "px");
    maxHeightRef.current = nextHeight;
    state.styles.popper.maxHeight = nextHeight;
    state.styles.popper.maxWidth = "".concat(width - EXTRA_SPACE, "px");
    return state;
  };
};

function Popper(props) {
  var _attributes$popper;

  var containerSelector = props.containerSelector,
      _props$offset = props.offset,
      offset = _props$offset === void 0 ? [0, 0] : _props$offset,
      _props$maxHeight = props.maxHeight,
      maxHeight = _props$maxHeight === void 0 ? MAX_HEIGHT : _props$maxHeight,
      _props$onHide = props.onHide,
      onHide = _props$onHide === void 0 ? noop : _props$onHide,
      _props$onOutsideClick = props.onOutsideClick,
      onOutsideClick = _props$onOutsideClick === void 0 ? noop : _props$onOutsideClick,
      _props$onShow = props.onShow,
      onShow = _props$onShow === void 0 ? noop : _props$onShow,
      _props$placement = props.placement,
      placement = _props$placement === void 0 ? "bottom-start" : _props$placement,
      _props$showing = props.showing,
      showing = _props$showing === void 0 ? true : _props$showing,
      transitionProps = props.transitionProps,
      unrestrictedHeight = props.unrestrictedHeight,
      _props$zIndex = props.zIndex,
      zIndex = _props$zIndex === void 0 ? zIndex1050 : _props$zIndex;
  var isMounted = useIsMounted();

  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      showingTransition = _React$useState2[0],
      setShowingTransition = _React$useState2[1];

  React.useEffect(function () {
    if (showing) setShowingTransition(true);
  }, [showing]);
  var contentRef = React.useRef();
  var triggerRef = React.useRef();
  var maxHeightRef = React.useRef("");
  var getMaxSizeModifier = React.useCallback(getMaxSize, []);
  var setMaxSizeModifier = React.useCallback(setMaxSize({
    maxHeight: maxHeight,
    maxHeightRef: maxHeightRef,
    unrestrictedHeight: unrestrictedHeight
  }), [maxHeight, unrestrictedHeight]);

  var _React$useState3 = React.useState(null),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      referenceElement = _React$useState4[0],
      setReferenceElement = _React$useState4[1];

  var _React$useState5 = React.useState(null),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      popperElement = _React$useState6[0],
      setPopperElement = _React$useState6[1];

  var _React$useState7 = React.useState(null),
      _React$useState8 = _slicedToArray(_React$useState7, 2),
      arrowElement = _React$useState8[0],
      setArrowElement = _React$useState8[1];

  var _usePopper = usePopper(referenceElement, popperElement, {
    modifiers: [{
      name: "getMaxSize",
      enabled: true,
      phase: "main",
      requiresIfExists: ["offset", "preventOverflow", "flip"],
      fn: getMaxSizeModifier
    }, {
      name: "setMaxSize",
      enabled: true,
      phase: "beforeWrite",
      requiresIfExists: ["getMaxSize"],
      fn: setMaxSizeModifier
    }, {
      name: "arrow",
      options: {
        element: arrowElement
      }
    }, {
      name: "offset",
      options: {
        offset: offset
      }
    }, {
      name: "preventOverflow",
      options: {
        padding: 8
      }
    }],
    placement: placement
  }),
      attributes = _usePopper.attributes,
      styles = _usePopper.styles,
      forceUpdate = _usePopper.forceUpdate;

  var referenceElementRectRef = React.useRef("");
  React.useEffect(function () {
    if (!referenceElement) return;

    var _referenceElement$get = referenceElement === null || referenceElement === void 0 ? void 0 : referenceElement.getBoundingClientRect(),
        top = _referenceElement$get.top,
        right = _referenceElement$get.right,
        bottom = _referenceElement$get.bottom,
        left = _referenceElement$get.left;

    var nextRect = "".concat(top).concat(right).concat(bottom).concat(left);
    if (nextRect === referenceElementRectRef.current) return;
    referenceElementRectRef.current = nextRect;
    if (forceUpdate) forceUpdate();
  });

  var _useThemeContext = useThemeContext(),
      defaultPortalContainerSelector = _useThemeContext.defaultPortalContainerSelector;

  function getPortalEl() {
    var selector = containerSelector || defaultPortalContainerSelector;
    if (!selector) return document.body;
    return document.querySelector(selector) || document.body;
  }

  var popperPlacement = (attributes === null || attributes === void 0 ? void 0 : (_attributes$popper = attributes.popper) === null || _attributes$popper === void 0 ? void 0 : _attributes$popper["data-popper-placement"]) || placement;
  React.useEffect(function () {
    var handleOutsideClick = function handleOutsideClick(e) {
      var target = e.target;
      if (referenceElement && referenceElement.contains(target)) return;
      if (contentRef.current && contentRef.current.contains(target)) return;
      onOutsideClick(e);
    };

    document.addEventListener("click", handleOutsideClick, true);
    return function () {
      document.removeEventListener("click", handleOutsideClick, true);
    };
  }, [onOutsideClick, referenceElement]);
  var popper = showing || showingTransition ? /*#__PURE__*/jsx("div", _objectSpread(_objectSpread({
    className: props.className,
    ref: setPopperElement,
    style: _objectSpread(_objectSpread({}, styles.popper), {}, {
      zIndex: zIndex
    })
  }, attributes.popper), {}, {
    children: /*#__PURE__*/jsx(Transition, {
      appear: true,
      transition: transitionProps,
      in: showing,
      mountOnEnter: true,
      unmountOnExit: true,
      onEntered: onShow,
      onExited: function onExited() {
        if (!isMounted.current) return;
        setShowingTransition(false);
        onHide();
      },
      children: function children(_ref4) {
        var style = _ref4.style,
            transitionRef = _ref4.ref;
        return /*#__PURE__*/jsxs("div", {
          style: _objectSpread(_objectSpread({}, style), {}, {
            position: "relative",
            maxHeight: maxHeightRef.current,
            transformOrigin: popperPlacement.includes("bottom") ? "top" : "bottom"
          }),
          ref: transitionRef,
          children: [/*#__PURE__*/jsx(ArrowPositioner, {
            ref: setArrowElement,
            style: styles.arrow,
            placement: popperPlacement,
            children: props.renderArrow
          }), /*#__PURE__*/jsx(Resizer, {
            onResize: forceUpdate || noop,
            ref: contentRef,
            children: function children(_ref5) {
              var ref = _ref5.ref;
              return props.renderContent({
                ref: ref,
                style: {
                  boxSizing: "border-box",
                  display: "block",
                  maxHeight: maxHeightRef.current
                },
                triggerRef: triggerRef
              });
            }
          })]
        });
      }
    })
  })) : null;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [props.renderTrigger({
      ref: setReferenceElement
    }), popper && (props.inline ? popper : /*#__PURE__*/ReactDOM.createPortal(popper, getPortalEl()))]
  });
}

Popper.propTypes = {
  "className": _PropTypes.string,
  "containerSelector": _PropTypes.string,
  "inline": _PropTypes.bool,
  "maxHeight": _PropTypes.number,
  "offset": _PropTypes.oneOfType([_PropTypes.func, _PropTypes.arrayOf(_PropTypes.number)]),
  "onHide": _PropTypes.func,
  "onOutsideClick": _PropTypes.func,
  "onShow": _PropTypes.func,
  "placement": _PropTypes.oneOf(["auto-end", "auto-start", "auto", "bottom-end", "bottom-start", "bottom", "left-end", "left-start", "left", "right-end", "right-start", "right", "top-end", "top-start", "top"]),
  "renderArrow": _PropTypes.func,
  "renderContent": _PropTypes.func.isRequired,
  "renderTrigger": _PropTypes.func.isRequired,
  "showing": _PropTypes.bool,
  "transitionProps": _PropTypes.oneOfType([_PropTypes.oneOf(["fade", "slide"]), _PropTypes.shape({
    "entered": _PropTypes.shape({
      "style": _PropTypes.object.isRequired,
      "transition": _PropTypes.shape({
        "delay": _PropTypes.number,
        "duration": _PropTypes.number,
        "easing": _PropTypes.oneOfType([_PropTypes.oneOf(["-moz-initial", "ease-in-out", "ease-in", "ease-out", "ease", "inherit", "initial", "linear", "revert", "step-end", "step-start", "unset"]), _PropTypes.object]),
        "property": _PropTypes.string
      })
    }).isRequired,
    "entering": _PropTypes.shape({
      "style": _PropTypes.object.isRequired,
      "transition": _PropTypes.shape({
        "delay": _PropTypes.number,
        "duration": _PropTypes.number,
        "easing": _PropTypes.oneOfType([_PropTypes.oneOf(["-moz-initial", "ease-in-out", "ease-in", "ease-out", "ease", "inherit", "initial", "linear", "revert", "step-end", "step-start", "unset"]), _PropTypes.object]),
        "property": _PropTypes.string
      })
    }).isRequired,
    "exited": _PropTypes.shape({
      "style": _PropTypes.object.isRequired,
      "transition": _PropTypes.shape({
        "delay": _PropTypes.number,
        "duration": _PropTypes.number,
        "easing": _PropTypes.oneOfType([_PropTypes.oneOf(["-moz-initial", "ease-in-out", "ease-in", "ease-out", "ease", "inherit", "initial", "linear", "revert", "step-end", "step-start", "unset"]), _PropTypes.object]),
        "property": _PropTypes.string
      })
    }),
    "exiting": _PropTypes.shape({
      "style": _PropTypes.object.isRequired,
      "transition": _PropTypes.shape({
        "delay": _PropTypes.number,
        "duration": _PropTypes.number,
        "easing": _PropTypes.oneOfType([_PropTypes.oneOf(["-moz-initial", "ease-in-out", "ease-in", "ease-out", "ease", "inherit", "initial", "linear", "revert", "step-end", "step-start", "unset"]), _PropTypes.object]),
        "property": _PropTypes.string
      })
    }).isRequired,
    "unmounted": _PropTypes.shape({
      "style": _PropTypes.object.isRequired,
      "transition": _PropTypes.shape({
        "delay": _PropTypes.number,
        "duration": _PropTypes.number,
        "easing": _PropTypes.oneOfType([_PropTypes.oneOf(["-moz-initial", "ease-in-out", "ease-in", "ease-out", "ease", "inherit", "initial", "linear", "revert", "step-end", "step-start", "unset"]), _PropTypes.object]),
        "property": _PropTypes.string
      })
    })
  })]),
  "unrestrictedHeight": _PropTypes.bool,
  "zIndex": _PropTypes.number
};
Popper.displayName = "Popper";

export { Popper };
